'use client'
import { format, isAfter, isBefore, parse, parseISO } from 'date-fns'
import { generateCalendarWeeks } from './generateCalendarWeeks'
import { addCollectiveTimeSlots } from './addTimeslotToRules'
import isPast from 'date-fns/isPast'
import isSameDay from 'date-fns/isSameDay'
import * as S from './calendly.styles'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRightIcon, ExternalLinkIcon } from '@/component-library'

class CalendarHelp {
  today: Date
  busyTimes: []
  calendlySchedule: []
  month: Date[][]
  constructor(calendlySchedule, busyTimes) {
    const today = new Date()

    this.today = today
    this.calendlySchedule = calendlySchedule
    this.busyTimes = busyTimes
    this.month = this.getMonth()
  }

  isPast(d: Date) {
    return isPast(d) && !isSameDay(d, this.today)
  }

  getMonth(): Date[][] {
    this.month = generateCalendarWeeks(this.today)
    return this.month
  }

  isTimeFree(time: Date) {
    if (time < new Date()) {
      return false
    }
 
    return !this.busyTimes.find(
      ({ start_time, end_time }) => isAfter(time, new Date(start_time)) && isBefore(time, new Date(end_time))
    )
  }
  getTimeslots(activeDate?: Date) {
    if (!activeDate) {
      return []
    }
    const rules = this.calendlySchedule.flatMap(({ rules }) => rules)

    const { timeslots } = addCollectiveTimeSlots(rules).find(({ wday }) => dayMap[wday] === activeDate.getDay()) || {
      timeslots: [],
    }


    return timeslots.filter((time) => {
      const dateString = format(activeDate, 'yyyy-MM-dd')
      const combinedDateTimeString = `${dateString}T${normalizeTimeslot(time)}`
      const localDate = parseISO(combinedDateTimeString)
      return this.isTimeFree(localDate)
    })
  }
  isLastDate(rowIndex: number, colIndex: number): boolean {
    return rowIndex === this.month.length - 1 && colIndex === this.month[rowIndex].length - 1
  }
}
const CalendarTable = ({ activeDate, setActiveDate, calendlySchedule, busyTimes }) => {
  const helper = new CalendarHelp(calendlySchedule, busyTimes),
    month = helper.getMonth(),
    itemRefs = useRef(new Map())

  const setRef = useCallback((element, id) => {
    if (element) {
      itemRefs.current.set(id, element)
    } else {
      itemRefs.current.delete(id)
    }
  }, [])

  return (
    <S.Table aria-label="Select a Day">
      <thead>
        <tr>
          <th>Mon</th>
          <th>Tue</th>
          <th>Wed</th>
          <th>Thu</th>
          <th>Fri</th>
          <th>Sat</th>
          <th>Sun</th>
        </tr>
      </thead>

      <S.Tbody id="calendar-table">
        {month.map((week, i) => (
          <S.Tr key={i}>
            {week.map(
              (day: Date, j, $: any) => (
                ($ = {
                  id: `${i}-${j}`,
                  past: helper.isPast(day),
                  date: day.getDate(),
                  string: day.toDateString(),
                  active: activeDate === day.toDateString(),
                  hasAppointments: helper.getTimeslots(day)?.length !== 0,
                  tabIndex: activeDate === day.toDateString() ? 2 : 1,
                  isLastDay: helper.isLastDate(i, j),
                }),
                !$.isLastDay ? (
                  <S.Td role="gridcell" key={$.id}>
                    <div>
                      <span onClick={() => setActiveDate('')}>{$.date}</span>
                    </div>
                    <S.Button
                      ref={(el) => setRef(el, $.id)}
                      tabIndex={$.tabIndex}
                      aria-label={`${format(day, 'EEEE, MMMM d')} ${$.past ? ' - No times available' : 'select date'}`}
                      type="button"
                      disabled={$.past}
                      className={$.hasAppointments ? '' : 'noAppointments'}
                      $active={$.active}
                      $cellPlace={[i, j]}
                      onClick={() => {
                        const target = itemRefs.current.get($.id)
                        target.style.setProperty('--translate-offset-center', S.moveCellPx(i, j))
                        target.style.setProperty('--translate-offset-end-md', S.moveCellPx(i, j, -1, 7, 16, 0))
                        target.style.setProperty('--translate-offset-end-sm', S.moveCellPx(i, j, 5, 0, -28, -4))

                        setActiveDate($.active ? '' : $.string)
                      }}
                    >
                      <span>{$.date}</span>

                      {$.active && <sup>{format(day, 'do').replace(/^\d+/, '')}</sup>}
                    </S.Button>
                  </S.Td>
                ) : (
                  <S.Td key={$.id}>
                    <S.Button
                      $cellPlace={[i, j]}
                      onClick={() => window.open('https://calendly.com/ric-lavers/30min', '_blank')}
                    >
                      <ArrowRightIcon width={24} fill="white" />
                    </S.Button>
                  </S.Td>
                )
              )
            )}
          </S.Tr>
        ))}
      </S.Tbody>
    </S.Table>
  )
}

export const CalendlyBooking = ({ calendlySchedule, busyTimes }) => {
  const helper = new CalendarHelp(calendlySchedule, busyTimes)

  const [animationName, s_animationName] = useState<S.AnimationName>('close')
  const [timeslots, s_timeslots] = useState([])
  const [activeDate, s_activeDate] = useState('')
  const prevDate = useRef('')
  useEffect(() => {
    prevDate.current = activeDate
  }, [activeDate])

  const first = prevDate.current === ''

  useEffect(() => {
    if (animationName === 'open') {
      s_timeslots(activeDate ? helper.getTimeslots(new Date(activeDate)) : [])
    }
  }, [activeDate, animationName])

  function setActiveDate(dateString: string) {
    if (animationName === 'open') {
      s_animationName('close')
    } else {
      s_animationName('open')
    }

    s_activeDate(dateString)
  }

  return (
    <S.Article>
      <h2>Lets catch up</h2>
      <S.Container>
        <CalendarTable
          activeDate={activeDate}
          setActiveDate={setActiveDate}
          calendlySchedule={calendlySchedule}
          busyTimes={busyTimes}
        />
        <S.Section
          key={activeDate}
          $animationName={animationName}
          onAnimationEnd={(props) => {
            if (props.animationName === 'close') {
              s_animationName('open')
            }
          }}
          $first={first}
          $visible={Boolean(activeDate)}
        >
          <div id="date">{activeDate && <h3>{format(new Date(activeDate), "'of' MMMM yyyy")}</h3>}</div>

          {timeslots.length === 0 && (
            <S.TimeSlot style={{ gridColumn: 'span 8', pointerEvents: 'none', userSelect: 'none' }}>
              No available times
            </S.TimeSlot>
          )}
          {timeslots.map(
            (time, i, date: any) => (
              (date = activeDate ? new Date(activeDate) : new Date()),
              (
                <S.TimeSlot
                  key={time + i}
                  tabIndex={2}
                  aria-label={`Select appointment for ${time} on ${format(date, 'EEEE MMMM yyyy')} in new tab`}
                  onClick={() => {
                    window.open(
                      `https://calendly.com/ric-lavers/30min/${generateUrlWithDateTime(date, time)}`,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }}
                >
                  <time>{time}</time>
                </S.TimeSlot>
              )
            )
          )}
        </S.Section>
        <S.Small>
          theme inspired by{' '}
          <a href="https://www.google.com/search?q=Sydney+Jacaranda+spring&udm=2" target="_blank">
            Sydney Jacaranda spring{' '}
          </a>
        </S.Small>
      </S.Container>
    </S.Article>
  )
}

export const ErrorCalendly = () => {
  return (
    <S.Article>
      <h2>Lets catch up</h2>
      <S.Article>
        <S.A href={'https://calendly.com/ric-lavers/30min'} target="_blank">
          <h3>Head to my Calendly and lets find a time we are both free</h3>
          <ExternalLinkIcon width={16} />
        </S.A>
      </S.Article>
    </S.Article>
  )
}

const dayMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

const generateUrlWithDateTime = (dateObj, timeslotTime) => {
  // 1. Combine the date (YYYY-MM-DD) from the Date object
  //    and the time (HH:mm:ss) from the timeslot string.
  const dateString = format(dateObj, 'yyyy-MM-dd')
  const combinedDateTimeString = `${dateString}T${normalizeTimeslot(timeslotTime)}`

  // 2. Parse this string back into a Date object.
  //    When parsed this way without an offset, JavaScript treats it as a local time.
  const localDate = parseISO(combinedDateTimeString)

  // 3. Format the local date object directly using the 'XXX' format specifier.
  //    'XXX' asks date-fns to use the host environment's current offset.
  //    This bypasses the date-fns-tz library entirely.
  const formattedDateTime = format(localDate, "yyyy-MM-dd'T'HH:mm:ssXXX")

  // 4. Extract parts for the query parameters
  const monthParam = format(localDate, 'yyyy-MM')
  const dateParam = format(localDate, 'yyyy-MM-dd')

  // 5. Construct the final URL string
  const finalUrl = `${formattedDateTime}?month=${monthParam}&date=${dateParam}`

  return finalUrl
}

const normalizeTimeslot = (timeString) => {
  // Try to parse the time string.
  // We assume a common base date for parsing the time component only (e.g., today's date).
  // The format 'h:mma' handles "9:00am", 'H:mm' handles "14:30", etc.

  // A robust approach to handle variations:
  const formatsToTry = ['h:mma', 'h:mm a', 'H:mm', 'HH:mm']
  let parsedTime

  for (const fmt of formatsToTry) {
    parsedTime = parse(timeString, fmt, new Date())
    if (parsedTime.toString() !== 'Invalid Date') {
      break // Stop if we successfully parsed it
    }
  }

  if (!parsedTime || parsedTime.toString() === 'Invalid Date') {
    console.error(`Could not parse timeslot time: ${timeString}`)
    // Fallback or throw an error as appropriate
    return null
  }

  // Format the parsed time object back into the precise "HH:mm:ss" string needed later
  return format(parsedTime, 'HH:mm:ss')
}
